import React, { useEffect, useRef, useState, useCallback } from "react";

const RichTextEditor = ({ value, onChange, style = {} }) => {
  const editorRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeFormats, setActiveFormats] = useState({});
  const [currentFontSize, setCurrentFontSize] = useState("3");
  const [currentTextColor, setCurrentTextColor] = useState("#000000");

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current) {
      const currentContent = editorRef.current.innerHTML;
      const newValue = value || "";

      if (currentContent !== newValue && !isFocused) {
        editorRef.current.innerHTML = newValue;
      }
    }
  }, [value, isFocused]);

  // Check active formatting states
  const updateActiveFormats = useCallback(() => {
    if (!editorRef.current) return;

    try {
      const formats = {
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikeThrough: document.queryCommandState("strikeThrough"),
        justifyLeft: document.queryCommandState("justifyLeft"),
        justifyCenter: document.queryCommandState("justifyCenter"),
        justifyRight: document.queryCommandState("justifyRight"),
        insertOrderedList: document.queryCommandState("insertOrderedList"),
        insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      };

      setActiveFormats(formats);

      // Get current font size
      const fontSize = document.queryCommandValue("fontSize") || "3";
      setCurrentFontSize(fontSize);

      // Get current text color
      const textColor = document.queryCommandValue("foreColor") || "#000000";
      setCurrentTextColor(textColor);
    } catch (error) {
      console.warn("Error checking command states:", error);
    }
  }, []);

  const formatText = (command, val = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, val);
      updateContent();
      updateActiveFormats();
    }
  };

  const updateContent = () => {
    if (editorRef.current && onChange) {
      const newContent = editorRef.current.innerHTML;
      onChange(newContent);
    }
  };

  const insertList = (type) => {
    formatText(
      type === "ordered" ? "insertOrderedList" : "insertUnorderedList"
    );
  };

  const insertLink = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    const url = prompt("Enter URL:", "https://");

    if (url && url !== "https://") {
      if (selectedText) {
        formatText("createLink", url);
      } else {
        const linkText = prompt("Enter link text:", url);
        if (linkText) {
          const linkHtml = `<a href="${url}" target="_blank">${linkText}</a>`;
          formatText("insertHTML", linkHtml);
        }
      }
    }
  };

  const insertTable = () => {
    const rows = prompt("Number of rows:", "2");
    const cols = prompt("Number of columns:", "2");

    if (rows && cols && !isNaN(rows) && !isNaN(cols)) {
      let tableHtml =
        '<table border="1" style="border-collapse: collapse; width: 100%;">';
      for (let i = 0; i < parseInt(rows); i++) {
        tableHtml += "<tr>";
        for (let j = 0; j < parseInt(cols); j++) {
          tableHtml +=
            '<td style="padding: 8px; border: 1px solid #ccc;">&nbsp;</td>';
        }
        tableHtml += "</tr>";
      }
      tableHtml += "</table>";
      formatText("insertHTML", tableHtml);
    }
  };

  const handleKeyDown = (e) => {
    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case "b":
          e.preventDefault();
          formatText("bold");
          break;
        case "i":
          e.preventDefault();
          formatText("italic");
          break;
        case "u":
          e.preventDefault();
          formatText("underline");
          break;
        case "z":
          e.preventDefault();
          formatText("undo");
          break;
        case "y":
          e.preventDefault();
          formatText("redo");
          break;
        default:
          break;
      }
    }
  };

  const handleSelectionChange = () => {
    updateActiveFormats();
  };

  useEffect(() => {
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, []);

  const getButtonStyle = (isActive = false) => ({
    padding: "6px 10px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    background: isActive ? "#007bff" : "white",
    color: isActive ? "white" : "#495057",
    cursor: "pointer",
    fontSize: "11px",
    userSelect: "none",
    minWidth: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    fontWeight: isActive ? "600" : "normal",
    boxShadow: isActive ? "0 2px 4px rgba(0,123,255,0.3)" : "none",
  });

  const toolbarStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    padding: "8px",
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #dee2e6",
    borderTopLeftRadius: "6px",
    borderTopRightRadius: "6px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15)",
  };

  const editorStyle = {
    minHeight: isExpanded ? "150px" : "100px",
    maxHeight: isExpanded ? "300px" : "100px",
    padding: "12px",
    outline: "none",
    overflowY: "auto",
    fontSize: "13px",
    lineHeight: "1.5",
    backgroundColor: "white",
    borderBottomLeftRadius: "6px",
    borderBottomRightRadius: "6px",
    border: "none",
    color: "#495057",
    fontFamily: "system-ui, -apple-system, sans-serif",
    ...style,
  };

  const selectStyle = {
    padding: "2px 4px",
    border: "1px solid #ced4da",
    borderRadius: "3px",
    fontSize: "11px",
    backgroundColor: "white",
    color: "#495057",
    height: "28px",
    cursor: "pointer",
  };

  const dividerStyle = {
    width: "1px",
    height: "20px",
    backgroundColor: "#dee2e6",
    margin: "0 4px",
    alignSelf: "center",
  };

  return (
    <div
      style={{
        border: "1px solid #ced4da",
        borderRadius: "6px",
        backgroundColor: "white",
        boxShadow: isFocused
          ? "0 0 0 0.2rem rgba(0,123,255,.25)"
          : "0 1px 3px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease",
      }}
    >
      {/* Enhanced Toolbar */}
      <div style={toolbarStyle}>
        {/* Text Formatting Group */}
        <div style={{ display: "flex", gap: "2px" }}>
          <button
            type="button"
            style={getButtonStyle(activeFormats.bold)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("bold")}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            style={getButtonStyle(activeFormats.italic)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("italic")}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            style={getButtonStyle(activeFormats.underline)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("underline")}
            title="Underline (Ctrl+U)"
          >
            <u>U</u>
          </button>
          <button
            type="button"
            style={getButtonStyle(activeFormats.strikeThrough)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("strikeThrough")}
            title="Strikethrough"
          >
            <s>S</s>
          </button>
        </div>

        <div style={dividerStyle}></div>

        {/* Font Size */}
        <select
          style={selectStyle}
          value={currentFontSize}
          onChange={(e) => {
            setCurrentFontSize(e.target.value);
            formatText("fontSize", e.target.value);
          }}
          onMouseDown={(e) => e.preventDefault()}
          title="Font Size"
        >
          <option value="1">8pt</option>
          <option value="2">10pt</option>
          <option value="3">12pt</option>
          <option value="4">14pt</option>
          <option value="5">18pt</option>
          <option value="6">24pt</option>
          <option value="7">36pt</option>
        </select>

        {/* Text Color */}
        <input
          type="color"
          value={currentTextColor}
          onChange={(e) => {
            setCurrentTextColor(e.target.value);
            formatText("foreColor", e.target.value);
          }}
          style={{
            width: "32px",
            height: "28px",
            border: "1px solid #ced4da",
            borderRadius: "4px",
            cursor: "pointer",
            padding: "1px",
          }}
          title="Text Color"
        />

        <div style={dividerStyle}></div>

        {/* Alignment Group */}
        <div style={{ display: "flex", gap: "2px" }}>
          <button
            type="button"
            style={getButtonStyle(activeFormats.justifyLeft)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("justifyLeft")}
            title="Align Left"
          >
            <span style={{ fontSize: "12px" }}>⬅</span>
          </button>
          <button
            type="button"
            style={getButtonStyle(activeFormats.justifyCenter)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("justifyCenter")}
            title="Center"
          >
            <span style={{ fontSize: "12px" }}>⬌</span>
          </button>
          <button
            type="button"
            style={getButtonStyle(activeFormats.justifyRight)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("justifyRight")}
            title="Align Right"
          >
            <span style={{ fontSize: "12px" }}>➡</span>
          </button>
        </div>

        <div style={dividerStyle}></div>

        {/* Lists Group */}
        <div style={{ display: "flex", gap: "2px" }}>
          <button
            type="button"
            style={getButtonStyle(activeFormats.insertUnorderedList)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertList("unordered")}
            title="Bullet List"
          >
            <span style={{ fontSize: "10px" }}>• List</span>
          </button>
          <button
            type="button"
            style={getButtonStyle(activeFormats.insertOrderedList)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertList("ordered")}
            title="Numbered List"
          >
            <span style={{ fontSize: "10px" }}>1. List</span>
          </button>
        </div>

        <div style={dividerStyle}></div>

        {/* Advanced Tools */}
        <div style={{ display: "flex", gap: "2px" }}>
          <button
            type="button"
            style={getButtonStyle()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertLink}
            title="Insert Link"
          >
            <span style={{ fontSize: "12px" }}>🔗</span>
          </button>
          <button
            type="button"
            style={getButtonStyle()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertTable}
            title="Insert Table"
          >
            <span style={{ fontSize: "12px" }}>⊞</span>
          </button>
          <button
            type="button"
            style={getButtonStyle()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("insertHorizontalRule")}
            title="Insert Line"
          >
            <span style={{ fontSize: "12px" }}>―</span>
          </button>
        </div>

        <div style={dividerStyle}></div>

        {/* Utility Tools */}
        <div style={{ display: "flex", gap: "2px" }}>
          <button
            type="button"
            style={getButtonStyle()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("removeFormat")}
            title="Clear Formatting"
          >
            <span style={{ fontSize: "9px" }}>Clear</span>
          </button>
          <button
            type="button"
            style={getButtonStyle()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("undo")}
            title="Undo (Ctrl+Z)"
          >
            <span style={{ fontSize: "12px" }}>↶</span>
          </button>
          <button
            type="button"
            style={getButtonStyle()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText("redo")}
            title="Redo (Ctrl+Y)"
          >
            <span style={{ fontSize: "12px" }}>↷</span>
          </button>
        </div>

        <div style={dividerStyle}></div>

        {/* Expand/Collapse */}
        <button
          type="button"
          style={getButtonStyle(isExpanded)}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? "Collapse Editor" : "Expand Editor"}
        >
          <span style={{ fontSize: "12px" }}>{isExpanded ? "⬇" : "⬆"}</span>
        </button>
      </div>

      {/* Enhanced Editor */}
      <div
        ref={editorRef}
        contentEditable={true}
        style={editorStyle}
        onInput={updateContent}
        onFocus={() => {
          setIsFocused(true);
          updateActiveFormats();
        }}
        onBlur={() => {
          setIsFocused(false);
          updateContent();
        }}
        onKeyDown={handleKeyDown}
        onMouseUp={updateActiveFormats}
        onKeyUp={updateActiveFormats}
        onPaste={(e) => {
          setTimeout(() => {
            updateContent();
            updateActiveFormats();
          }, 10);
        }}
        suppressContentEditableWarning={true}
        data-placeholder="Start typing your content here..."
      />

      {/* Enhanced Styling */}
      <style jsx>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #6c757d;
          cursor: text;
          font-style: italic;
        }
        [contenteditable]:focus {
          outline: none;
        }
        [contenteditable] table {
          border-collapse: collapse;
          width: 100%;
          margin: 8px 0;
        }
        [contenteditable] table td {
          border: 1px solid #ddd;
          padding: 8px;
          min-width: 50px;
        }
        [contenteditable] a {
          color: #007bff;
          text-decoration: underline;
        }
        [contenteditable] ul,
        [contenteditable] ol {
          margin: 8px 0;
          padding-left: 20px;
        }
        [contenteditable] li {
          margin: 4px 0;
        }
        [contenteditable] hr {
          border: none;
          border-top: 1px solid #ccc;
          margin: 12px 0;
        }
        [contenteditable] blockquote {
          border-left: 4px solid #007bff;
          padding-left: 12px;
          margin: 8px 0;
          color: #6c757d;
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
